import { type ReactNode } from "react";
import { docT } from "./doc-locale";
import { jumpToDocId } from "./DocEmail";
import "./doc-webinar.css";

// The webinar document leaves Pavel approved in F91 (2026-08-07): the funnel
// of the customer journey and the page wireframe with REAL copy. They live
// next to DocEmail.tsx under the same three laws: nothing is injected (these
// are components the whitelist in DocHtml.tsx decides to place), every
// affordance answers hover, keyboard AND tap, and a control that cannot do
// its job is not drawn as a control - a funnel stage whose target does not
// exist is a plain card, never a dead button.
//
// The evening (the part F93 rebuilt) lives in DocEvening.tsx and is
// re-exported here, so every import site keeps one path into the webinar
// document language.
export {
  DocArc, DocEveningBlock, DocMinutes, DocScope, DocSeam,
  minuteLabel, parseMinutes,
  type DocArcMove, type DocEveningPhase, type DocMinuteRow
} from "./DocEvening";

/* ------------------------------------------------------------------ funnel -- */

export interface DocFunnelStage {
  /** Visual role of the stage thumbnail: ad | reg | thanks | email | live | offer. */
  kind: string;
  name: string;
  /** The stage's job in one sentence ("Cíl: ..."). */
  goal: string;
  /** Target number / conversion benchmark shown as the stage metric. */
  num: string;
  note: string;
  /** Doc id the stage opens (its elaborated chapter part), or null. */
  target: string | null;
  /** Short inline description under the goal. */
  body: ReactNode;
  hasBody: boolean;
}

const FUNNEL_KINDS = new Set(["ad", "reg", "thanks", "email", "live", "offer"]);

/**
 * A miniature of the stage drawn in CSS: an ad card, a page with a form, a
 * thank-you page, an e-mail stack, a live screen, an offer card. Decorative
 * (aria-hidden): the words beside it carry the meaning, the thumbnail makes
 * the funnel read as PAGES on a path instead of six labeled boxes.
 */
function StageThumb(props: { kind: string }) {
  const kind = FUNNEL_KINDS.has(props.kind) ? props.kind : "reg";
  return (
    <span className={`doc-fstage-thumb doc-fstage-thumb-${kind}`} aria-hidden="true">
      {kind === "ad" && (<><i className="t-media" /><i className="t-bar w7" /><i className="t-bar w5" /><i className="t-btn" /></>)}
      {kind === "reg" && (<><i className="t-bar w4 c" /><i className="t-bar w8 c strong" /><i className="t-bar w6 c" /><i className="t-field" /><i className="t-btn c" /></>)}
      {kind === "thanks" && (<><i className="t-check" /><i className="t-bar w7 c" /><i className="t-bar w5 c" /></>)}
      {kind === "email" && (<><i className="t-mail" /><i className="t-mail" /><i className="t-mail" /></>)}
      {kind === "live" && (<><i className="t-media live" /><i className="t-dot-live" /><i className="t-bar w6" /></>)}
      {kind === "offer" && (<><i className="t-bar w5 c" /><i className="t-price" /><i className="t-btn c" /></>)}
    </span>
  );
}

/**
 * The webinar funnel: the customer's path drawn as page cards on a falling
 * axis, each carrying its GOAL and its target number, connected by flow
 * arrows. Pavel: "měl bys tady nakreslit propracovaný funnel, tak jak se
 * kreslej funnely a cesty zákazníka."
 */
export function DocFunnel(props: { title?: string; stages: DocFunnelStage[] }) {
  if (props.stages.length === 0) return null;
  return (
    <nav className="doc-funnel" aria-label={props.title || docT("doc.funnel.aria")}>
      {props.title && <p className="doc-funnel-title">{props.title}</p>}
      <ol className="doc-funnel-track">
        {props.stages.map((stage, index) => {
          const inner = (
            <>
              <StageThumb kind={stage.kind} />
              <span className="doc-fstage-main">
                <span className="doc-fstage-name">{stage.name}</span>
                {stage.goal && (
                  <span className="doc-fstage-goal">
                    <span className="doc-fstage-goal-label">{docT("doc.funnel.goal")}</span>
                    {stage.goal}
                  </span>
                )}
                {stage.hasBody && <span className="doc-fstage-body">{stage.body}</span>}
                {stage.note && <span className="doc-fstage-note">{stage.note}</span>}
              </span>
              {stage.num && <span className="doc-fstage-num">{stage.num}</span>}
            </>
          );
          return (
            <li className="doc-fstage-slot" key={`${stage.name}-${index}`} style={{ "--fi": index } as React.CSSProperties}>
              {index > 0 && <span className="doc-fstage-flow" aria-hidden="true" />}
              {stage.target ? (
                <button
                  type="button"
                  className="doc-fstage is-live"
                  aria-label={`${docT("doc.funnel.jump")}: ${stage.name}`}
                  onClick={() => jumpToDocId(stage.target as string)}
                >
                  {inner}
                </button>
              ) : (
                // No section to open: a button here would be dead UI.
                <span className="doc-fstage">{inner}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* --------------------------------------------------------------- wireframe -- */

/**
 * A page wireframe the reader can BELIEVE: a miniature of the real page in a
 * browser frame, with the actual copy in the actual hierarchy - pre-title,
 * title, subtitle, bullets, form, button. Pavel: "opravdu skvěle zpracovaný
 * wireframe toho, jak může ta landing page vypadat, vložený v tom dokumentu."
 * Never grey placeholder boxes: the builder writes FULL copy into the blocks.
 *
 * The children arrive pre-rendered by DocHtml.tsx (the renderer owns inline
 * markup and the nothing-vanishes law); this component owns the page chrome.
 */
export function DocWireframe(props: {
  id?: string;
  /** reg (registration) | thanks (thank-you); styling hook only. */
  kind: string;
  /** Caption above the frame: which variant / which page this is. */
  name?: string;
  /** One sentence under the caption: why this name works. */
  note?: string;
  children: ReactNode;
}) {
  const kind = props.kind === "thanks" ? "thanks" : "reg";
  return (
    <figure id={props.id} className={`doc-wf doc-wf-${kind}`}>
      {(props.name || props.note) && (
        <figcaption className="doc-wf-caption">
          {props.name && <span className="doc-wf-name">{props.name}</span>}
          {props.note && <span className="doc-wf-note">{props.note}</span>}
        </figcaption>
      )}
      <div className="doc-wf-frame" role="img" aria-label={props.name ? `${docT("doc.wf.aria")}: ${props.name}` : docT("doc.wf.aria")}>
        <div className="doc-wf-chrome" aria-hidden="true">
          <span className="doc-wf-dot" /><span className="doc-wf-dot" /><span className="doc-wf-dot" />
          <span className="doc-wf-address" />
        </div>
        <div className="doc-wf-page">{props.children}</div>
      </div>
    </figure>
  );
}

/** The form block inside a wireframe: labeled fields + the real button copy. */
export function DocWfForm(props: { fields: string[]; button?: string; note?: string }) {
  return (
    <div className="doc-wf-form">
      {props.fields.map((field, index) => (
        <span className="doc-wf-field" key={`${field}-${index}`}>{field}</span>
      ))}
      {props.button && <span className="doc-wf-button">{props.button}</span>}
      {props.note && <span className="doc-wf-formnote">{props.note}</span>}
    </div>
  );
}

